// import { auth } from "@/firebase/firebase";
// import { getFirebaseAuthToken } from "@/utils/firebaseSession";
// import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";

// const apiHost =
// 	process.env.NEXT_PUBLIC_PRODUCTION_HOST ||
// 	process.env.NEXT_PUBLIC_DEVELOPMENT_HOST;
// const defaultApiVersion = "v1";

// const axiosInstance: AxiosInstance = axios.create({
// 	baseURL: `${apiHost}/api/${defaultApiVersion}`,
// });

// // Request Interceptor
// import type { InternalAxiosRequestConfig } from "axios";

// declare module "axios" {
// 	interface AxiosRequestConfig {
// 		apiVersion?: string;
// 	}
// }

// type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & {
// 	_retryWithFreshToken?: boolean;
// };

// axiosInstance.interceptors.request.use(
// 	async (
// 		config: InternalAxiosRequestConfig
// 	): Promise<InternalAxiosRequestConfig> => {
// 		try {
// 			const apiVersion = config.apiVersion || defaultApiVersion;
// 			config.baseURL = `${apiHost}/api/${apiVersion}`;

// 			// Ensure every request can reuse the current Firebase session or
// 			// bootstrap an anonymous one before hitting the API.
// 			const token = await getFirebaseAuthToken();

// 			// Ensure headers is always defined
// 			if (!config.headers) {
// 				config.headers = {} as import("axios").AxiosRequestHeaders;
// 			}

// 			if (token) {
// 				config.headers.Authorization = `Bearer ${token}`;
// 			}

// 			// Ensure content-type is set correctly
// 			if (config.data instanceof FormData) {
// 				config.headers["Content-Type"] = "multipart/form-data";
// 			} else {
// 				config.headers["Content-Type"] = "application/json";
// 			}

// 			return config;
// 		} catch (error) {
// 			console.error("Error getting auth token:", error);
// 			return Promise.reject(error);
// 		}
// 	},
// 	(error: AxiosError) => Promise.reject(error)
// );

// // Response Interceptor
// axiosInstance.interceptors.response.use(
// 	(response: AxiosResponse): AxiosResponse => response,
// 	async (error: AxiosError) => {
// 		const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

// 		if (
// 			error.response?.status === 401 &&
// 			originalRequest &&
// 			!originalRequest._retryWithFreshToken
// 		) {
// 			originalRequest._retryWithFreshToken = true;

// 			try {
// 				const freshToken = await getFirebaseAuthToken({ forceRefresh: true });

// 				if (freshToken) {
// 					if (!originalRequest.headers) {
// 						originalRequest.headers =
// 							{} as import("axios").AxiosRequestHeaders;
// 					}
// 					originalRequest.headers.Authorization = `Bearer ${freshToken}`;
// 					return axiosInstance(originalRequest);
// 				}
// 			} catch (retryError) {
// 				console.error("Error retrying request with a fresh token:", retryError);
// 			}
// 		}

// 		if (error.response?.status === 401) {
// 			console.error("Unauthorized request after token retry:", originalRequest?.url);
// 			if (
// 				typeof window !== "undefined" &&
// 				auth.currentUser &&
// 				!auth.currentUser.isAnonymous
// 			) {
// 				window.location.href = "/auth";
// 			}
// 		}
// 		return Promise.reject(error);
// 	}
// );

// export default axiosInstance;

import { auth } from "@/firebase/firebase";
import { getFirebaseAuthToken } from "@/utils/firebaseSession";
import { getSessionId } from "@/utils/sessionTracker";
import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";

const apiHost =
  process.env.NEXT_PUBLIC_PRODUCTION_HOST ||
  process.env.NEXT_PUBLIC_DEVELOPMENT_HOST;
const defaultApiVersion = "v1";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${apiHost}/api/${defaultApiVersion}`,
});

// ─── Cache ────────────────────────────────────────────────────────────────────

interface CacheEntry {
  data: unknown;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL_MS = 60_000; // 1 minute

function buildCacheKey(
  config: import("axios").InternalAxiosRequestConfig,
): string {
  const params = config.params ? JSON.stringify(config.params) : "";
  return `${config.method?.toUpperCase()}:${config.baseURL}${config.url}:${params}`;
}

function isCacheable(
  config: import("axios").InternalAxiosRequestConfig,
): boolean {
  return config.method?.toUpperCase() === "GET";
}

// ─── Type augmentation ────────────────────────────────────────────────────────

import type { InternalAxiosRequestConfig } from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    apiVersion?: string;
    /** Override per-request TTL in ms. Set to 0 to skip cache entirely. */
    cacheTTL?: number;
  }
}

type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retryWithFreshToken?: boolean;
};

// ─── Request Interceptor ──────────────────────────────────────────────────────

axiosInstance.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    try {
      const apiVersion = config.apiVersion || defaultApiVersion;
      config.baseURL = `${apiHost}/api/${apiVersion}`;

      // Cache check — only for GET requests
      if (isCacheable(config)) {
        const ttl = config.cacheTTL ?? DEFAULT_TTL_MS;

        if (ttl > 0) {
          const key = buildCacheKey(config);
          const entry = cache.get(key);

          if (entry && entry.expiresAt > Date.now()) {
            // Return a pre-resolved adapter so Axios never hits the network
            config.adapter = () =>
              Promise.resolve({
                data: entry.data,
                status: 200,
                statusText: "OK (cached)",
                headers: {},
                config,
              });
          }
        }
      }

      const token = await getFirebaseAuthToken();

      if (!config.headers) {
        config.headers = {} as import("axios").AxiosRequestHeaders;
      }

      const sessionId = getSessionId();
      if (sessionId) {
        config.headers["x-session-id"] = sessionId;
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }



      if (config.data instanceof FormData) {
        config.headers["Content-Type"] = "multipart/form-data";
      } else {
        config.headers["Content-Type"] = "application/json";
      }

      return config;
    } catch (error) {
      console.error("Error getting auth token:", error);
      return Promise.reject(error);
    }
  },
  (error: AxiosError) => Promise.reject(error),
);

// ─── Response Interceptor ─────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const config = response.config;

    // Populate cache for successful GET responses (skip already-cached ones)
    if (isCacheable(config) && response.statusText !== "OK (cached)") {
      const ttl = config.cacheTTL ?? DEFAULT_TTL_MS;

      if (ttl > 0) {
        const key = buildCacheKey(config);
        cache.set(key, { data: response.data, expiresAt: Date.now() + ttl });
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | RetryableAxiosRequestConfig
      | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retryWithFreshToken
    ) {
      originalRequest._retryWithFreshToken = true;

      try {
        const freshToken = await getFirebaseAuthToken({ forceRefresh: true });

        if (freshToken) {
          if (!originalRequest.headers) {
            originalRequest.headers = {} as import("axios").AxiosRequestHeaders;
          }
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (retryError) {
        console.error("Error retrying request with a fresh token:", retryError);
      }
    }

    if (error.response?.status === 401) {
      console.error(
        "Unauthorized request after token retry:",
        originalRequest?.url,
      );
      if (
        typeof window !== "undefined" &&
        auth.currentUser &&
        !auth.currentUser.isAnonymous
      ) {
        window.location.href = "/auth";
      }
    }

    return Promise.reject(error);
  },
);

// ─── Cache utilities (export if needed elsewhere) ─────────────────────────────

/** Manually invalidate a specific GET URL from the cache. */
export function invalidateCache(url: string): void {
  for (const key of cache.keys()) {
    if (key.includes(url)) cache.delete(key);
  }
}

/** Wipe the entire cache (e.g. on logout). */
export function clearCache(): void {
  cache.clear();
}

export default axiosInstance;
